// PostgreSQL Service - Replaces Supabase for frontend operations
// All database operations go through the Express API backend

import { apiCall, clearCache } from "../config/postgres";

// localStorage initialization removed - using PostgreSQL database instead

// PostgreSQL API for courses
export const courseAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/courses");
            return data || [];
        } catch (error) {
            console.error("Error fetching courses:", error);
            return [];
        }
    },

    add: async (course) => {
        try {
            const data = await apiCall("/api/courses", {
                method: "POST",
                body: JSON.stringify(course),
            });
            return data;
        } catch (error) {
            console.error("Error adding course:", error);
            throw error;
        }
    },

    update: async (updatedCourse) => {
        try {
            const data = await apiCall(`/api/courses/${updatedCourse.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedCourse),
            });
            return data;
        } catch (error) {
            console.error("Error updating course:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/courses/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting course:", error);
            throw error;
        }
    },

    updateStatus: async (id, status) => {
        try {
            const data = await apiCall(`/api/donations/${id}/status`, {
                method: "PUT",
                body: JSON.stringify({ status }),
            });
            clearCache("/api/donations");
            clearCache("/api/donations/stats");
            return data;
        } catch (error) {
            console.error("Error updating donation status:", error);
            throw error;
        }
    },
};

// PostgreSQL API for teachers
export const teacherAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/teachers");
            return data || [];
        } catch (error) {
            console.error("Error fetching teachers:", error);
            return [];
        }
    },

    add: async (teacher) => {
        try {
            const data = await apiCall("/api/teachers", {
                method: "POST",
                body: JSON.stringify(teacher),
            });
            return data;
        } catch (error) {
            console.error("Error adding teacher:", error);
            throw error;
        }
    },

    update: async (updatedTeacher) => {
        try {
            const data = await apiCall(`/api/teachers/${updatedTeacher.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedTeacher),
            });
            return data;
        } catch (error) {
            console.error("Error updating teacher:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/teachers/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting teacher:", error);
            throw error;
        }
    },
};

// PostgreSQL API for students
export const studentAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/students");
            return data || [];
        } catch (error) {
            console.error("Error fetching students:", error);
            return [];
        }
    },

    add: async (student) => {
        try {
            const data = await apiCall("/api/students", {
                method: "POST",
                body: JSON.stringify(student),
            });
            return data;
        } catch (error) {
            console.error("Error adding student:", error);
            throw error;
        }
    },

    update: async (updatedStudent) => {
        try {
            const data = await apiCall(`/api/students/${updatedStudent.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedStudent),
            });
            return data;
        } catch (error) {
            console.error("Error updating student:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/students/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting student:", error);
            throw error;
        }
    },
};

// PostgreSQL API for contact messages
export const messageAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/contact");
            return data || [];
        } catch (error) {
            console.error("Error fetching messages:", error);
            return [];
        }
    },

    getRecent: async (limit = 5) => {
        try {
            const messages = await messageAPI.getAll();
            return messages
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, limit);
        } catch (error) {
            console.error("Error getting recent messages:", error);
            return [];
        }
    },

    getUnreadCount: async () => {
        try {
            const messages = await messageAPI.getAll();
            return messages.filter((message) => !message.is_read).length;
        } catch (error) {
            console.error("Error getting unread count:", error);
            return 0;
        }
    },

    add: async (message) => {
        try {
            const data = await apiCall("/api/contact", {
                method: "POST",
                body: JSON.stringify(message),
            });
            return data;
        } catch (error) {
            console.error("Error adding message:", error);
            throw error;
        }
    },

    markAsRead: async (id) => {
        try {
            const data = await apiCall(`/api/contact/${id}/read`, {
                method: "PUT",
            });
            return data;
        } catch (error) {
            console.error("Error marking message as read:", error);
            throw error;
        }
    },

    markAllAsRead: async () => {
        try {
            await apiCall("/api/contact/read-all", {
                method: "PUT",
            });
            return true;
        } catch (error) {
            console.error("Error marking all messages as read:", error);
            throw error;
        }
    },
};

// PostgreSQL API for donations
export const donationAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/donations");
            return data || [];
        } catch (error) {
            console.error("Error fetching donations:", error);
            return [];
        }
    },

    getRecent: async (limit = 5) => {
        try {
            const donations = await donationAPI.getAll();
            return donations
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, limit);
        } catch (error) {
            console.error("Error getting recent donations:", error);
            return [];
        }
    },

    getTotalAmount: async () => {
        try {
            const donations = await donationAPI.getAll();
            return donations
                .filter((d) => d.status === "completed")
                .reduce(
                    (total, donation) => total + parseFloat(donation.amount),
                    0,
                );
        } catch (error) {
            console.error("Error calculating total donations:", error);
            return 0;
        }
    },

    getStats: async () => {
        try {
            const data = await apiCall("/api/donations/stats");
            return data || {};
        } catch (error) {
            console.error("Error fetching donation stats:", error);
            return {};
        }
    },

    create: async (donation) => {
        try {
            const payload = {
                amount: donation.amount,
                donor_name: donation.donorName || donation.donor_name,
                email: donation.email,
                message: donation.message || null,
                is_anonymous:
                    donation.isAnonymous ?? donation.is_anonymous ?? false,
                is_recurring:
                    donation.isRecurring ?? donation.is_recurring ?? false,
            };

            const data = await apiCall("/api/donations", {
                method: "POST",
                body: JSON.stringify(payload),
            });

            // Invalidate cached donation lists
            clearCache("/api/donations");
            clearCache("/api/donations/stats");

            return data;
        } catch (error) {
            console.error("Error creating donation:", error);
            throw error;
        }
    },

    updateStatus: async (id, status) => {
        try {
            const data = await apiCall(`/api/donations/${id}/status`, {
                method: "PUT",
                body: JSON.stringify({ status }),
            });
            clearCache("/api/donations");
            clearCache("/api/donations/stats");
            return data;
        } catch (error) {
            console.error("Error updating donation status:", error);
            throw error;
        }
    },
};

// PostgreSQL API for events
export const eventAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/scholarships");
            return data || [];
        } catch (error) {
            console.error("Error fetching events:", error);
            return [];
        }
    },

    getUpcoming: async (limit = 5) => {
        try {
            const events = await eventAPI.getAll();
            const today = new Date().toISOString().split("T")[0];
            return events
                .filter((event) => event.date >= today && event.is_active)
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .slice(0, limit);
        } catch (error) {
            console.error("Error fetching upcoming events:", error);
            return [];
        }
    },

    add: async (event) => {
        try {
            const data = await apiCall("/api/scholarships", {
                method: "POST",
                body: JSON.stringify(event),
            });
            return data;
        } catch (error) {
            console.error("Error adding event:", error);
            throw error;
        }
    },

    update: async (updatedEvent) => {
        try {
            const data = await apiCall(`/api/scholarships/${updatedEvent.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedEvent),
            });
            return data;
        } catch (error) {
            console.error("Error updating event:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/scholarships/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting event:", error);
            throw error;
        }
    },
};

// PostgreSQL API for website settings
export const websiteSettingsAPI = {
    getSetting: async (key) => {
        try {
            const data = await apiCall(`/api/settings/${key}`);
            return data?.value || null;
        } catch (error) {
            console.error(`Error fetching setting ${key}:`, error);
            return null;
        }
    },

    setSetting: async (key, value) => {
        try {
            const data = await apiCall(`/api/settings/${key}`, {
                method: "POST",
                body: JSON.stringify({ value }),
            });
            return data;
        } catch (error) {
            console.error(`Error setting ${key}:`, error);
            throw error;
        }
    },

    getAllSettings: async () => {
        try {
            const data = await apiCall("/api/settings");
            return data || {};
        } catch (error) {
            console.error("Error fetching all settings:", error);
            return {};
        }
    },

    setMultipleSettings: async (settingsObject) => {
        try {
            const data = await apiCall("/api/settings", {
                method: "PUT",
                body: JSON.stringify(settingsObject),
            });
            return data;
        } catch (error) {
            console.error("Error setting multiple settings:", error);
            throw error;
        }
    },

    deleteSetting: async (key) => {
        try {
            await apiCall(`/api/settings/${key}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error(`Error deleting setting ${key}:`, error);
            throw error;
        }
    },
};

// Leadership API
export const leadershipAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/leadership");
            return data || [];
        } catch (error) {
            console.error("Error fetching leadership:", error);
            return [];
        }
    },

    add: async (leader) => {
        try {
            const data = await apiCall("/api/leadership", {
                method: "POST",
                body: JSON.stringify(leader),
            });
            return data;
        } catch (error) {
            console.error("Error adding leadership:", error);
            throw error;
        }
    },

    update: async (updatedLeader) => {
        try {
            const data = await apiCall(`/api/leadership/${updatedLeader.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedLeader),
            });
            return data;
        } catch (error) {
            console.error("Error updating leadership:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/leadership/${id}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error deleting leadership:", error);
            throw error;
        }
    },
};

// Staff API
export const staffAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/staff");
            return data || [];
        } catch (error) {
            console.error("Error fetching staff:", error);
            return [];
        }
    },

    add: async (staff) => {
        try {
            const data = await apiCall("/api/staff", {
                method: "POST",
                body: JSON.stringify(staff),
            });
            return data;
        } catch (error) {
            console.error("Error adding staff:", error);
            throw error;
        }
    },

    update: async (updatedStaff) => {
        try {
            const data = await apiCall(`/api/staff/${updatedStaff.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedStaff),
            });
            return data;
        } catch (error) {
            console.error("Error updating staff:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/staff/${id}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error deleting staff:", error);
            throw error;
        }
    },
};

// Course Management API (assign teachers/students)
export const courseManagementAPI = {
    // Assign teacher to course
    assignTeacher: async (courseId, teacherId, isPrimary = false) => {
        try {
            const data = await apiCall(`/api/courses/${courseId}/teachers`, {
                method: "POST",
                body: JSON.stringify({
                    teacher_id: teacherId,
                    is_primary: isPrimary,
                }),
            });
            return data;
        } catch (error) {
            console.error("Error assigning teacher:", error);
            throw error;
        }
    },

    // Remove teacher from course
    removeTeacher: async (courseId, teacherId) => {
        try {
            await apiCall(`/api/courses/${courseId}/teachers/${teacherId}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error removing teacher:", error);
            throw error;
        }
    },

    // Get teachers for a course
    getCourseTeachers: async (courseId) => {
        try {
            const data = await apiCall(`/api/courses/${courseId}/teachers`);
            return data || [];
        } catch (error) {
            console.error("Error fetching course teachers:", error);
            return [];
        }
    },

    // Assign student to course
    assignStudent: async (
        courseId,
        studentId,
        enrollmentDate,
        status = "active",
    ) => {
        try {
            const data = await apiCall(`/api/courses/${courseId}/students`, {
                method: "POST",
                body: JSON.stringify({
                    student_id: studentId,
                    enrollment_date: enrollmentDate,
                    status,
                }),
            });
            return data;
        } catch (error) {
            console.error("Error assigning student:", error);
            throw error;
        }
    },

    // Remove student from course
    removeStudent: async (courseId, studentId) => {
        try {
            await apiCall(`/api/courses/${courseId}/students/${studentId}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error removing student:", error);
            throw error;
        }
    },

    // Get students for a course
    getCourseStudents: async (courseId) => {
        try {
            const data = await apiCall(`/api/courses/${courseId}/students`);
            return data || [];
        } catch (error) {
            console.error("Error fetching course students:", error);
            return [];
        }
    },

    // Get courses for a teacher
    getTeacherCourses: async (teacherId) => {
        try {
            const data = await apiCall(`/api/teachers/${teacherId}/courses`);
            return data || [];
        } catch (error) {
            console.error("Error fetching teacher courses:", error);
            return [];
        }
    },

    // Get courses for a student
    getStudentCourses: async (studentId) => {
        try {
            const data = await apiCall(`/api/students/${studentId}/courses`);
            return data || [];
        } catch (error) {
            console.error("Error fetching student courses:", error);
            return [];
        }
    },
};

// Scores API
export const scoresAPI = {
    add: async (scoreData) => {
        try {
            const data = await apiCall("/api/scores", {
                method: "POST",
                body: JSON.stringify(scoreData),
            });
            return data;
        } catch (error) {
            console.error("Error adding score:", error);
            throw error;
        }
    },

    getForStudent: async (courseId, studentId) => {
        try {
            const data = await apiCall(
                `/api/scores/course/${courseId}/student/${studentId}`,
            );
            return data || [];
        } catch (error) {
            console.error("Error fetching scores:", error);
            return [];
        }
    },

    getForCourse: async (courseId) => {
        try {
            const data = await apiCall(`/api/scores/course/${courseId}`);
            return data || [];
        } catch (error) {
            console.error("Error fetching course scores:", error);
            return [];
        }
    },

    update: async (scoreId, scoreData) => {
        try {
            const data = await apiCall(`/api/scores/${scoreId}`, {
                method: "PUT",
                body: JSON.stringify(scoreData),
            });
            return data;
        } catch (error) {
            console.error("Error updating score:", error);
            throw error;
        }
    },

    delete: async (scoreId) => {
        try {
            await apiCall(`/api/scores/${scoreId}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error deleting score:", error);
            throw error;
        }
    },
};

// Attendance API
export const attendanceAPI = {
    add: async (attendanceData) => {
        try {
            const data = await apiCall("/api/attendance", {
                method: "POST",
                body: JSON.stringify(attendanceData),
            });
            return data;
        } catch (error) {
            console.error("Error adding attendance:", error);
            throw error;
        }
    },

    getForStudent: async (courseId, studentId, month, year) => {
        try {
            const params = new URLSearchParams();
            if (month) params.append("month", month);
            if (year) params.append("year", year);
            const query = params.toString() ? `?${params.toString()}` : "";
            const data = await apiCall(
                `/api/attendance/course/${courseId}/student/${studentId}${query}`,
            );
            return data || [];
        } catch (error) {
            console.error("Error fetching attendance:", error);
            return [];
        }
    },

    getForCourse: async (courseId, date) => {
        try {
            const query = date ? `?date=${date}` : "";
            const data = await apiCall(
                `/api/attendance/course/${courseId}${query}`,
            );
            return data || [];
        } catch (error) {
            console.error("Error fetching course attendance:", error);
            return [];
        }
    },

    update: async (attendanceId, attendanceData) => {
        try {
            const data = await apiCall(`/api/attendance/${attendanceId}`, {
                method: "PUT",
                body: JSON.stringify(attendanceData),
            });
            return data;
        } catch (error) {
            console.error("Error updating attendance:", error);
            throw error;
        }
    },

    delete: async (attendanceId) => {
        try {
            await apiCall(`/api/attendance/${attendanceId}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error("Error deleting attendance:", error);
            throw error;
        }
    },
};

// User Account API (for creating teacher/student accounts)
export const userAccountAPI = {
    getTeacherUsers: async () => {
        try {
            const data = await apiCall("/api/auth/users/teachers");
            return data || [];
        } catch (error) {
            console.error("Error fetching teacher users:", error);
            return [];
        }
    },

    getStudentUsers: async () => {
        try {
            const data = await apiCall("/api/auth/users/students");
            return data || [];
        } catch (error) {
            console.error("Error fetching student users:", error);
            return [];
        }
    },

    createTeacherAccount: async (teacherId, username, password, email) => {
        try {
            const data = await apiCall("/api/auth/signup/teacher", {
                method: "POST",
                body: JSON.stringify({
                    teacher_id: teacherId,
                    username,
                    password,
                    email,
                }),
            });
            return data;
        } catch (error) {
            console.error("Error creating teacher account:", error);
            throw error;
        }
    },

    createStudentAccount: async (studentId, username, password, email) => {
        try {
            const data = await apiCall("/api/auth/signup/student", {
                method: "POST",
                body: JSON.stringify({
                    student_id: studentId,
                    username,
                    password,
                    email,
                }),
            });
            return data;
        } catch (error) {
            console.error("Error creating student account:", error);
            throw error;
        }
    },

    // create admin account (only allowed for existing admins)
    createAdminAccount: async (username, password) => {
        try {
            const data = await apiCall("/api/auth/users/admins", {
                method: "POST",
                body: JSON.stringify({ username, password }),
            });
            return data;
        } catch (error) {
            console.error("Error creating admin account:", error);
            throw error;
        }
    },

    getAdminUsers: async () => {
        try {
            const data = await apiCall("/api/auth/users/admins");
            return data || [];
        } catch (error) {
            console.error("Error fetching admin users:", error);
            return [];
        }
    },

    updateAccount: async (userType, userId, accountData) => {
        try {
            const data = await apiCall(
                `/api/auth/users/${userType}/${userId}`,
                {
                    method: "PUT",
                    body: JSON.stringify(accountData),
                },
            );
            return data;
        } catch (error) {
            console.error(`Error updating ${userType} account:`, error);
            throw error;
        }
    },

    deleteAccount: async (userType, userId) => {
        try {
            await apiCall(`/api/auth/users/${userType}/${userId}`, {
                method: "DELETE",
            });
            return true;
        } catch (error) {
            console.error(`Error deleting ${userType} account:`, error);
            throw error;
        }
    },
};

// PostgreSQL API for top students
export const topStudentsAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/top-students");
            return data || [];
        } catch (error) {
            console.error("Error fetching top students:", error);
            return [];
        }
    },

    getFeatured: async () => {
        try {
            const data = await apiCall("/api/top-students/featured");
            return data || [];
        } catch (error) {
            console.error("Error fetching featured top students:", error);
            return [];
        }
    },

    getById: async (id) => {
        try {
            const data = await apiCall(`/api/top-students/${id}`);
            return data;
        } catch (error) {
            console.error("Error fetching top student:", error);
            throw error;
        }
    },

    add: async (topStudent) => {
        try {
            const data = await apiCall("/api/top-students", {
                method: "POST",
                body: JSON.stringify(topStudent),
            });
            return data;
        } catch (error) {
            console.error("Error adding top student:", error);
            throw error;
        }
    },

    update: async (updatedTopStudent) => {
        try {
            const data = await apiCall(
                `/api/top-students/${updatedTopStudent.id}`,
                {
                    method: "PUT",
                    body: JSON.stringify(updatedTopStudent),
                },
            );
            return data;
        } catch (error) {
            console.error("Error updating top student:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/top-students/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting top student:", error);
            throw error;
        }
    },
};

// PostgreSQL API for posts (news/blog)
export const postAPI = {
    getAll: async () => {
        try {
            const data = await apiCall("/api/posts");
            return data || [];
        } catch (error) {
            console.error("Error fetching posts:", error);
            return [];
        }
    },

    getById: async (id) => {
        try {
            const data = await apiCall(`/api/posts/${id}`);
            return data;
        } catch (error) {
            console.error("Error fetching post:", error);
            throw error;
        }
    },

    add: async (post) => {
        try {
            const data = await apiCall("/api/posts", {
                method: "POST",
                body: JSON.stringify(post),
            });
            return data;
        } catch (error) {
            console.error("Error adding post:", error);
            throw error;
        }
    },

    update: async (updatedPost) => {
        try {
            const data = await apiCall(`/api/posts/${updatedPost.id}`, {
                method: "PUT",
                body: JSON.stringify(updatedPost),
            });
            return data;
        } catch (error) {
            console.error("Error updating post:", error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            await apiCall(`/api/posts/${id}`, {
                method: "DELETE",
            });
            return id;
        } catch (error) {
            console.error("Error deleting post:", error);
            throw error;
        }
    },
};
